import { useState, useEffect, useRef } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "@/lib/constants";

// Google "G" logo (multi-colour)
const GoogleG = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8a12 12 0 1 1 0-24c3 0 5.7 1.1 7.8 3l5.6-5.6A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 3l5.6-5.6A20 20 0 0 0 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3a12 12 0 0 1-17.7-6.3l-6.6 5C10 39.6 16.5 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.5l6.3 5.3C42 35 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/>
  </svg>
);

export default function TestimonialsCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = TESTIMONIALS.length;
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 6500);
    return () => clearInterval(timerRef.current);
  }, [paused, total]);

  const t = TESTIMONIALS[index];

  const goTo = (newIdx) => setIndex(((newIdx % total) + total) % total);

  return (
    <section
      className="section-pad bg-white border-y border-[#0E2A5C]/8"
      data-testid="testimonials-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="container-x">
        <div className="text-center mb-12">
          <div className="eyebrow mb-4 justify-center inline-flex">What people say</div>
          <h2 className="text-[32px] md:text-[42px] font-bold text-[#0E2A5C] leading-tight">
            Trusted by clients across NSW.
          </h2>
          <div className="mt-5 inline-flex items-center gap-3 bg-[#F7F8F3] border border-[#0E2A5C]/10 rounded-full px-5 py-2">
            <GoogleG size={18} />
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }, (_, i) => `top-star-${i}`).map((id) => (
                <Star key={id} size={15} className="fill-[#FBBC04] text-[#FBBC04]" />
              ))}
            </div>
            <span className="text-[13px] text-[#0E2A5C] font-semibold">5.0 · {total} Google reviews</span>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative max-w-3xl mx-auto">
          {/* Card */}
          <div
            key={t.id}
            className="bg-white rounded-3xl p-8 md:p-12 border border-[#0E2A5C]/10 shadow-[0_24px_60px_-30px_rgba(14,42,92,0.3)] relative"
            data-testid={`testimonial-${t.id}`}
          >
            <Quote size={40} className="text-[#A6CD3C]/30 absolute top-7 left-7" />

            {/* Stars */}
            <div className="flex gap-1 mb-6 justify-center">
              {Array.from({ length: t.rating }, (_, i) => `${t.id}-star-${i}`).map((id) => (
                <Star key={id} size={20} className="fill-[#FBBC04] text-[#FBBC04]" />
              ))}
            </div>

            {/* Quote */}
            <p className="text-[17px] md:text-[19px] text-[#0B0F1A]/85 leading-relaxed text-center font-medium italic">
              "{t.quote}"
            </p>

            {/* Author */}
            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0E2A5C] to-[#3B6CA8] flex items-center justify-center text-white font-bold text-[16px] shadow-md">
                {t.author.charAt(0)}
              </div>
              <div className="text-left">
                <div className="text-[15px] font-semibold text-[#0E2A5C]">{t.author}</div>
                {t.role && <div className="text-[12.5px] text-[#5B6478]">{t.role}</div>}
              </div>
              <div className="border-l border-[#0E2A5C]/15 pl-4 flex items-center gap-2">
                <GoogleG size={20} />
                <div className="text-[11px] text-[#5B6478] uppercase tracking-wider font-semibold">via Google</div>
              </div>
            </div>
          </div>

          {/* Arrows */}
          <button
            onClick={() => goTo(index - 1)}
            aria-label="Previous testimonial"
            className="absolute left-0 lg:-left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-[#0E2A5C]/15 text-[#0E2A5C] hover:bg-[#0E2A5C] hover:text-white shadow-md transition-all flex items-center justify-center"
            data-testid="testimonial-prev"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => goTo(index + 1)}
            aria-label="Next testimonial"
            className="absolute right-0 lg:-right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-[#0E2A5C]/15 text-[#0E2A5C] hover:bg-[#0E2A5C] hover:text-white shadow-md transition-all flex items-center justify-center"
            data-testid="testimonial-next"
          >
            <ChevronRight size={20} />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2.5 mt-8">
            {TESTIMONIALS.map((item, i) => (
              <button
                key={item.id}
                onClick={() => goTo(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === index ? "bg-[#A6CD3C] w-8" : "bg-[#0E2A5C]/20 hover:bg-[#0E2A5C]/40 w-2"
                }`}
                data-testid={`testimonial-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
